import { FEATURE_FLAG_DEFINITIONS } from './featureFlagDefinitions';
import { FEATURE_FLAGS, FeatureFlag, FeatureFlagsState } from './featureFlags';
import { loadFeatureFlags, saveFeatureFlags } from './featureStorage';

export const ENABLE_EXPERIMENT_PARAM = 'enable_experiment';
export const DISABLE_EXPERIMENT_PARAM = 'disable_experiment';

/** Path that turns one flag on or off, e.g. `/?enable_experiment=newLandingPage`. */
export function experimentTogglePath(flag: FeatureFlag, enabled: boolean): string {
  const param = enabled ? ENABLE_EXPERIMENT_PARAM : DISABLE_EXPERIMENT_PARAM;
  return `/?${new URLSearchParams({ [param]: flag }).toString()}`;
}

export function resolveExperimentFlag(name: string): FeatureFlag | undefined {
  if (FEATURE_FLAGS.includes(name as FeatureFlag)) {
    return name as FeatureFlag;
  }

  const byLabel = FEATURE_FLAG_DEFINITIONS.find((def) => def.label === name);
  return byLabel?.key;
}

export function applyExperimentQueryParams(
  flags: FeatureFlagsState,
  searchParams: URLSearchParams
): { flags: FeatureFlagsState; changed: boolean; hadExperimentParams: boolean } {
  const enableRaw = searchParams.get(ENABLE_EXPERIMENT_PARAM);
  const disableRaw = searchParams.get(DISABLE_EXPERIMENT_PARAM);
  const hadExperimentParams = enableRaw !== null || disableRaw !== null;

  if (!hadExperimentParams) {
    return { flags, changed: false, hadExperimentParams: false };
  }

  const next: FeatureFlagsState = { ...flags };
  let changed = false;

  // Apply enable first, then disable so disable wins on conflict.
  const enableFlag = enableRaw ? resolveExperimentFlag(enableRaw) : undefined;
  if (enableFlag && !next[enableFlag]) {
    next[enableFlag] = true;
    changed = true;
  }

  const disableFlag = disableRaw ? resolveExperimentFlag(disableRaw) : undefined;
  if (disableFlag && next[disableFlag]) {
    next[disableFlag] = false;
    changed = true;
  }

  return { flags: next, changed, hadExperimentParams };
}

export function stripExperimentQueryParams(): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  if (
    !url.searchParams.has(ENABLE_EXPERIMENT_PARAM) &&
    !url.searchParams.has(DISABLE_EXPERIMENT_PARAM)
  ) {
    return;
  }

  url.searchParams.delete(ENABLE_EXPERIMENT_PARAM);
  url.searchParams.delete(DISABLE_EXPERIMENT_PARAM);

  const search = url.searchParams.toString();
  const next = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
  window.history.replaceState(window.history.state, '', next);
}

/**
 * Load flags from storage and apply enable/disable_experiment URL params
 * synchronously. Must run before module-level `isFeatureEnabled` consumers
 * (e.g. client.nav) so nav/routes pick up the flag on the first load.
 */
export function loadInitialFeatureFlags(): FeatureFlagsState {
  const stored = loadFeatureFlags();
  if (typeof window === 'undefined') return stored;

  const { flags, changed, hadExperimentParams } = applyExperimentQueryParams(
    stored,
    new URLSearchParams(window.location.search)
  );

  if (changed) {
    saveFeatureFlags(flags);
  }
  if (hadExperimentParams) {
    stripExperimentQueryParams();
  }

  return flags;
}
